import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Building2,
  Settings2,
  Users,
  MapPin,
  CheckCircle2,
  ClipboardCheck,
} from "lucide-react";
import { Linkedin } from "lucide-react";
import { CALENDLY_URL, LINKEDIN_URL, JAKE_LINKEDIN_URL } from "../lib/site";
import Seo from "../components/Seo";
import Tm from "../components/ow/Tm";
import { businessSchema, websiteSchema } from "../lib/seoSchemas";
import { useContent } from "../lib/content";

const pillarMeta = [
  { icon: Building2, eyebrow: "01", to: "/services", k: "home.pillar1" },
  { icon: Settings2, eyebrow: "02", to: "/services", k: "home.pillar2" },
  { icon: Users, eyebrow: "03", to: "/services", k: "home.pillar3" },
  { icon: ClipboardCheck, eyebrow: "04", to: "/services/operator-walk", k: "home.pillar4", tm: true },
];

const statKeys = ["home.stat1", "home.stat2", "home.stat3", "home.stat4", "home.stat5", "home.stat6", "home.stat7", "home.stat8", "home.stat9", "home.stat10"];
const clientKeys = ["home.client1", "home.client2", "home.client3", "home.client4", "home.client5"];

export default function Home() {
  const { t } = useContent();
  const headline = t("home.hero.headline");
  return (
    <div data-testid="page-home">
      <Seo
        title={t("home.seo.title")}
        description={t("home.seo.description")}
        schemas={[businessSchema, websiteSchema]}
      />
      {/* HERO */}
      <section
        className="relative overflow-hidden bg-white"
        data-testid="home-hero"
      >
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 0% 0%, rgba(191,167,255,0.18), transparent 40%), radial-gradient(circle at 100% 100%, rgba(243,232,255,0.6), transparent 40%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-24 lg:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <div className="eyebrow mb-6" data-testid="hero-eyebrow">
                {t("home.hero.eyebrow")}
              </div>
              <h1
                className="font-serif text-5xl sm:text-6xl lg:text-7xl text-brand-700 leading-[1.02] tracking-tight"
                data-testid="hero-headline"
              >
                {headline.split("'").map((part, i, arr) =>
                  i < arr.length - 1 ? (
                    <span key={i}>
                      {part}
                      <span className="text-brand-400">{"\u2019"}</span>
                    </span>
                  ) : (
                    <span key={i}>{part}</span>
                  )
                )}
              </h1>
              <p
                className="mt-8 text-lg sm:text-xl text-ink-soft max-w-2xl leading-relaxed"
                data-testid="hero-subheadline"
              >
                {t("home.hero.subheadline")}
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-7 py-4 text-sm font-medium tracking-wide transition-colors group"
                  data-testid="hero-book-call-btn"
                >
                  {t("home.hero.cta")}
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <Link
                  to="/services"
                  className="inline-flex items-center gap-2 text-brand-700 text-sm font-medium tracking-wide border-b border-brand-700 pb-1 hover:border-brand-400 transition-colors"
                  data-testid="hero-view-services-link"
                >
                  {t("home.hero.services_link")}
                </Link>
              </div>
            </div>

            <div className="lg:col-span-4 lg:pl-8 lg:border-l lg:border-ink-line">
              <div className="text-[10px] tracking-[0.25em] uppercase text-brand-500 mb-4">
                {t("home.hero.side_eyebrow")}
              </div>
              <p className="font-serif text-2xl text-brand-700 leading-snug">
                {t("home.hero.side_text")}
              </p>
              <div className="mt-6 flex items-center gap-2 text-sm text-ink-muted">
                <MapPin className="w-4 h-4 text-brand-400" />
                {t("home.hero.location_line")}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE PILLARS */}
      <section
        className="bg-white py-24 lg:py-32 border-t border-ink-line"
        data-testid="home-services"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <div className="eyebrow mb-4">{t("home.services.eyebrow")}</div>
              <h2 className="font-serif text-4xl sm:text-5xl text-brand-700 leading-tight whitespace-pre-line">
                {t("home.services.heading")}
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 flex items-end">
              <p className="text-lg text-ink-soft leading-relaxed">
                {t("home.services.intro")}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-ink-line border border-ink-line">
            {pillarMeta.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.k}
                  className="bg-white p-10 lg:p-12 flex flex-col h-full"
                  data-testid={`pillar-${p.eyebrow}`}
                >
                  <div className="flex items-center justify-between mb-10">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-brand-400">
                      {p.eyebrow}
                    </div>
                    <Icon className="w-6 h-6 text-brand-700" />
                  </div>
                  <h3 className="font-serif text-2xl text-brand-700 mb-4 leading-tight">
                    {t(`${p.k}.title`)}{p.tm && <Tm />}
                  </h3>
                  <p className="text-ink-soft leading-relaxed flex-1">{t(`${p.k}.body`)}</p>
                  <Link
                    to={p.to}
                    className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-brand-700 group"
                    data-testid={`pillar-link-${p.eyebrow}`}
                  >
                    Learn more
                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TRACK RECORD */}
      <section
        className="bg-brand-100 py-24 lg:py-32"
        data-testid="home-track-record"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-16">
            <div className="eyebrow mb-4">{t("home.track.eyebrow")}</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-brand-700 leading-tight">
              {t("home.track.heading")}
            </h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-brand-200">
            {statKeys.map((k, i) => (
              <div
                key={k}
                className="bg-brand-100 p-6 lg:p-7 flex flex-col h-full"
                data-testid={`stat-${i}`}
              >
                <div className="font-serif text-5xl lg:text-6xl text-brand-700 leading-none">
                  {t(`${k}.value`)}
                </div>
                <div className="mt-4 text-xs text-ink-soft uppercase tracking-[0.18em] leading-snug">
                  {t(`${k}.label`)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE WORK WITH */}
      <section className="bg-white py-24 lg:py-32" data-testid="home-clients">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="eyebrow mb-4">{t("home.clients.eyebrow")}</div>
              <h2 className="font-serif text-4xl sm:text-5xl text-brand-700 leading-tight">
                {t("home.clients.heading")}
              </h2>
              <p className="mt-6 text-ink-soft leading-relaxed">
                {t("home.clients.intro")}
              </p>
            </div>
            <div className="lg:col-span-7 lg:pl-8 lg:border-l lg:border-ink-line">
              <ul className="space-y-5">
                {clientKeys.map((k, i) => (
                  <li
                    key={k}
                    className="flex items-start gap-4 group"
                    data-testid={`client-type-${i}`}
                  >
                    <CheckCircle2 className="w-5 h-5 text-brand-700 mt-0.5 shrink-0" />
                    <span className="text-lg text-ink-soft group-hover:text-brand-700 transition-colors">
                      {t(k)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* LEADERSHIP PREVIEW */}
      <section
        className="bg-brand-100 py-24 lg:py-32 border-t border-brand-200"
        data-testid="home-about-preview"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="eyebrow mb-4">{t("home.leadership.eyebrow")}</div>
          <h2 className="font-serif text-4xl sm:text-5xl text-brand-700 leading-tight max-w-3xl">
            {t("home.leadership.heading")}
          </h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16">
            <div data-testid="leadership-caleb">
              <div
                className="aspect-[4/5] bg-white overflow-hidden border border-brand-200"
                data-testid="about-preview-image"
              >
                <img
                  src="/images/caleb.png"
                  alt="Caleb R. Holder, DBA, founder of Holder Hospitality Consulting"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <h3 className="mt-8 font-serif text-3xl text-brand-700 leading-tight">
                {t("home.leadership.caleb_name")}
              </h3>
              <p className="mt-1 text-[11px] tracking-[0.2em] uppercase text-brand-500">
                {t("home.leadership.caleb_title")}
              </p>
              <p className="mt-4 text-ink-soft leading-relaxed">
                {t("home.leadership.caleb_bio")}
              </p>
              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-brand-700 text-sm font-medium hover:text-brand-500 transition-colors"
                data-testid="leadership-caleb-linkedin"
              >
                <Linkedin className="w-4 h-4" /> LinkedIn
              </a>
            </div>
            <div data-testid="leadership-jake">
              <div
                className="aspect-[4/5] bg-white overflow-hidden border border-brand-200"
                data-testid="about-preview-image-jake"
              >
                <img
                  src="/images/jake.png"
                  alt="Jake Lair, co-founder and COO of Holder Hospitality Consulting"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <h3 className="mt-8 font-serif text-3xl text-brand-700 leading-tight">
                {t("home.leadership.jake_name")}
              </h3>
              <p className="mt-1 text-[11px] tracking-[0.2em] uppercase text-brand-500">
                {t("home.leadership.jake_title")}
              </p>
              <p className="mt-4 text-ink-soft leading-relaxed">
                {t("home.leadership.jake_bio")}
              </p>
              <a
                href={JAKE_LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-brand-700 text-sm font-medium hover:text-brand-500 transition-colors"
                data-testid="leadership-jake-linkedin"
              >
                <Linkedin className="w-4 h-4" /> LinkedIn
              </a>
            </div>
          </div>
          <Link
            to="/about"
            className="mt-12 inline-flex items-center gap-2 text-brand-700 text-sm font-medium border-b border-brand-700 pb-1 hover:border-brand-400 transition-colors"
            data-testid="about-preview-link"
          >
            {t("home.leadership.link")}
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="bg-brand-700 text-white py-20" data-testid="home-cta">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h2 className="font-serif text-3xl sm:text-4xl leading-tight">
              {t("home.cta.heading")}
            </h2>
            <p className="mt-4 text-brand-100/90 max-w-xl">
              {t("home.cta.body")}
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-brand-700 hover:bg-brand-100 transition-colors px-7 py-4 text-sm font-medium"
              data-testid="home-cta-button"
            >
              {t("home.cta.button")} <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
